import { useState, useMemo, useEffect } from 'react';
import { calculateDeal } from '../../lib/nmtc/calculations';
import ScenarioPanel from './ScenarioPanel';
import StructureChart from './StructureChart';
import SourcesUsesTable from './SourcesUsesTable';
import TabNav from './TabNav';
import NetBenefitTab from './NetBenefitTab';
import UnwindMechanicsTab from './UnwindMechanicsTab';
import HowToStartTab from './HowToStartTab';
import NMTCJourneyBanner from './NMTCJourneyBanner';
import './nmtc.css';

// ── Default inputs ─────────────────────────────────────────────────────────

const DEFAULT_INPUTS = {
  taxCreditPrice: 0.85,
  ownerEquity: 1_000_000,
  hardDebt: 5_000_000,
  hardDebtType: 'bank',
  hardDebtRate: 6.5,
  loanTerm: 25,
  subAllocationPct: 4.0,
  locationQualifies: null,
  businessType: '',
  isNonProfit: null,
};

// ── Tab navigation helpers ─────────────────────────────────────────────────

const ORDERED_TABS = [
  'overview',
  'project-sizing',
  'sources-uses',
  'structure',
  'net-benefit',
  'unwind',
  'how-to-start',
];

const TAB_LABELS = {
  'overview':       'Overview',
  'project-sizing': 'Project Sizing',
  'sources-uses':   'Sources & Uses',
  'structure':      'Structure Chart',
  'net-benefit':    'Net Benefit',
  'unwind':         'Unwind Mechanics',
  'how-to-start':   'How to Get Started',
};

// ── Continue button ────────────────────────────────────────────────────────

function ContinueButton({ currentTab, onNavigate }) {
  const idx = ORDERED_TABS.indexOf(currentTab);
  if (idx < 0 || idx >= ORDERED_TABS.length - 1) return null;
  const next = ORDERED_TABS[idx + 1];

  return (
    <div className="py-12 px-4">
      <div className="mx-auto max-w-sm">
        <button
          onClick={() => onNavigate(next)}
          className="w-full flex items-center justify-center gap-3 bg-emerald-800 hover:bg-emerald-700 active:bg-emerald-900 text-white font-semibold py-4 px-6 rounded-2xl shadow-sm transition-colors group touch-manipulation text-base"
        >
          <span>Continue to {TAB_LABELS[next]}</span>
          <svg
            className="w-5 h-5 transition-transform group-hover:translate-x-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ── Section wrappers ───────────────────────────────────────────────────────

function TabSection({ bg = 'bg-gray-50', children }) {
  return (
    <section className={`${bg} py-10 px-4`}>
      <div className="mx-auto max-w-5xl">{children}</div>
    </section>
  );
}

function WideTabSection({ bg = 'bg-gray-50', children }) {
  return (
    <section className={`${bg} py-10 px-4`}>
      <div className="mx-auto max-w-7xl">{children}</div>
    </section>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function NMTCApp() {
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [activeTab, setActiveTab] = useState('overview');

  const calc = useMemo(() => calculateDeal(inputs), [inputs]);

  // Track the initial tab load (Overview) as a virtual page view
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: `/nmtc-model/${activeTab}`,
        page_title: `NMTC Model – ${TAB_LABELS[activeTab]}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Track tab changes as virtual page views in GA4
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: `/nmtc-model/${tab}`,
        page_title: `NMTC Model – ${TAB_LABELS[tab]}`,
      });
    }
  };

  return (
    <div id="top" className="min-h-screen flex flex-col bg-gray-50">

      {/* ── Hero */}
      <section className="bg-emerald-900 py-10 pb-14 px-4 text-white" style={{ backgroundColor: '#064e3b', color: '#fff' }}>
        <div className="mx-auto max-w-4xl text-center space-y-3">
          <h1 className="text-3xl md:text-4xl font-bold leading-tight tracking-tight">
            NMTC Deal Model
          </h1>
          <p className="text-emerald-200 text-base max-w-2xl mx-auto" style={{ color: '#a7f3d0' }}>
            Understand, size, and analyze a New Markets Tax Credit transaction — step by step.
          </p>
          <p className="text-emerald-400 text-xs max-w-xl mx-auto" style={{ color: '#6ee7b7' }}>
            For illustrative purposes only. Consult a qualified advisor before structuring a transaction.
          </p>
        </div>
      </section>

      {/* ── Tab Navigation (sticky) */}
      <div className="sticky top-0 z-40 shadow-sm bg-white">
        <div className="mx-auto max-w-5xl">
          <TabNav activeTab={activeTab} onTabChange={handleTabChange} />
        </div>
      </div>

      {/* ── Tab Content */}
      <div className="flex-1">

        {/* 1. Overview */}
        {activeTab === 'overview' && (
          <div>
            <TabSection bg="bg-white">
              <NMTCJourneyBanner />
            </TabSection>
            <ContinueButton currentTab="overview" onNavigate={handleTabChange} />
          </div>
        )}

        {/* 2. Project Sizing */}
        {activeTab === 'project-sizing' && (
          <div>
            <TabSection bg="bg-white">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Project Sizing</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Enter your project capital and deal parameters — QEI and structure are calculated instantly.
                </p>
              </div>
              <ScenarioPanel inputs={inputs} calc={calc} onChange={setInputs} />
            </TabSection>
            <ContinueButton currentTab="project-sizing" onNavigate={handleTabChange} />
          </div>
        )}

        {/* 3. Sources & Uses */}
        {activeTab === 'sources-uses' && (
          <div>
            <TabSection bg="bg-white">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Sources &amp; Uses</h2>
                <p className="text-sm text-gray-500 mt-1">
                  How capital flows into and out of your NMTC transaction — all fees itemized.
                </p>
              </div>
              <SourcesUsesTable calc={calc} inputs={inputs} />
            </TabSection>
            <ContinueButton currentTab="sources-uses" onNavigate={handleTabChange} />
          </div>
        )}

        {/* 4. Structure Chart */}
        {activeTab === 'structure' && (
          <div>
            <WideTabSection bg="bg-gray-50">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900">Deal Structure</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Flow of capital from sources through the NMTC intermediary structure to your project.
                </p>
              </div>
              <StructureChart calc={calc} inputs={inputs} />
            </WideTabSection>
            <ContinueButton currentTab="structure" onNavigate={handleTabChange} />
          </div>
        )}

        {/* 5. Net Benefit */}
        {activeTab === 'net-benefit' && (
          <div>
            <WideTabSection bg="bg-white">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900">Net Benefit Analysis</h2>
                <p className="text-sm text-gray-500 mt-1">
                  What this deal is actually worth to you — toggle fees on/off to stress-test the economics.
                </p>
              </div>
              <NetBenefitTab calc={calc} inputs={inputs} />
            </WideTabSection>
            <ContinueButton currentTab="net-benefit" onNavigate={handleTabChange} />
          </div>
        )}

        {/* 6. Unwind Mechanics */}
        {activeTab === 'unwind' && (
          <div>
            <WideTabSection bg="bg-gray-50">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900">Unwind Mechanics</h2>
                <p className="text-sm text-gray-500 mt-1">
                  What happens at Year 7 — and how to plan for it now.
                </p>
              </div>
              <UnwindMechanicsTab calc={calc} isNonProfit={inputs.isNonProfit} />
            </WideTabSection>
            <ContinueButton currentTab="unwind" onNavigate={handleTabChange} />
          </div>
        )}

        {/* 7. How to Get Started */}
        {activeTab === 'how-to-start' && (
          <HowToStartTab
            locationQualifies={inputs.locationQualifies}
            businessType={inputs.businessType}
            isNonProfit={inputs.isNonProfit}
            qei={calc.qei}
            calc={calc}
            inputs={inputs}
          />
        )}

      </div>
    </div>
  );
}
